/**
 * GovRAMP product list — V105-1-3 / ADR-0075 §3 (migration 452).
 *
 * A site editor uploads the govramp.org/product-list Excel export (no API; manual and on
 * demand until GovRAMP's terms are confirmed, owner decision Q3). The Worker parses it with
 * SheetJS, `parseGovrampProductList` checks the pinned header and Status vocabulary, and this
 * service files proposals for changed rows only, batched `govramp-products@<upload date>`:
 *   new row → create; changed → update; gone from the export → deprecate (the export has no
 *   stable id: a rename is a deprecate + create the reviewer pairs); held file → one shape
 *   review item on the source (stable change_id), no row proposals.
 * A FedRAMP link is suggested only on an exact (normalized) CSP + offering name match and is
 * set when the reviewer approves the row (Open question 6 default: name match + review).
 *
 * Global content (control plane): written only by `applyGovrampProductChange` after approval.
 */
import { ContentValidationError } from './framework-requirement-service.js';
import { provenance } from './fedramp-marketplace-service.js';
import { GOVRAMP_STATUS } from './govramp-product-list-parser.js';
export const normName = (v) => (v ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const s = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
const STATUSES = new Set(Object.values(GOVRAMP_STATUS));
/** `govramp_product` → govramp_products (upsert; deprecate soft-deletes). */
export async function applyGovrampProductChange(db, change, reviewer) {
    const prov = provenance(change, reviewer);
    const now = new Date().toISOString();
    if (change.change_type === 'deprecate') {
        if (!change.target_id)
            throw new ContentValidationError('deprecate requires target_id');
        await db.execute(`UPDATE govramp_products SET status = 'deprecated', reviewer = ?, reviewed_at = ?, updated_at = ? WHERE govramp_product_key = ?`, [prov.reviewer, prov.reviewed_at, now, change.target_id]);
        return change.target_id;
    }
    const raw = typeof change.proposed_state === 'string' ? JSON.parse(change.proposed_state) : change.proposed_state;
    const p = (raw ?? {});
    const key = s(p.govramp_product_key) ?? s(change.target_id);
    if (!key || !/^[0-9a-f]{16}$/.test(key))
        throw new ContentValidationError('govramp_product needs a 16-hex govramp_product_key');
    if (change.target_id && change.target_id !== key)
        throw new ContentValidationError('govramp_product_key must match target_id');
    const org = s(p.organization_name);
    const offering = s(p.service_offering);
    const statusRaw = s(p.status_raw);
    const status = s(p.govramp_status);
    if (!org || !offering || !statusRaw)
        throw new ContentValidationError('a GovRAMP row needs organization_name, service_offering and status_raw');
    if (!status || !STATUSES.has(status))
        throw new ContentValidationError(`bad govramp_status ${String(p.govramp_status)}`);
    const rank = s(p.impact_rank);
    if (rank && !['low', 'moderate', 'high'].includes(rank))
        throw new ContentValidationError(`bad impact_rank ${rank}`);
    const rowSha = s(p.row_sha256);
    const fileSha = s(p.file_sha256);
    if (!rowSha || !/^[0-9a-f]{64}$/.test(rowSha) || !fileSha || !/^[0-9a-f]{64}$/.test(fileSha))
        throw new ContentValidationError('a GovRAMP row carries row_sha256 and file_sha256');
    const retrieved = s(p.retrieved);
    const snapshot = s(p.snapshot_id);
    if (!retrieved || !snapshot)
        throw new ContentValidationError('a GovRAMP row records when and from which upload it came');
    await db.execute(`INSERT INTO govramp_products (govramp_product_key, organization_name, service_offering, service_model, impact_level, impact_rank,
       status_raw, govramp_status, fedramp_offering_id, row_sha256, file_sha256, retrieved, snapshot_id, status, source_kind, reviewer,
       reviewed_at, created_at, updated_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 'active', ?, ?, ?, ?, ?)
     ON CONFLICT (govramp_product_key) DO UPDATE SET
       organization_name = excluded.organization_name, service_offering = excluded.service_offering, service_model = excluded.service_model,
       impact_level = excluded.impact_level, impact_rank = excluded.impact_rank, status_raw = excluded.status_raw,
       govramp_status = excluded.govramp_status, fedramp_offering_id = excluded.fedramp_offering_id, row_sha256 = excluded.row_sha256,
       file_sha256 = excluded.file_sha256, retrieved = excluded.retrieved, snapshot_id = excluded.snapshot_id, status = 'active',
       source_kind = excluded.source_kind, reviewer = excluded.reviewer, reviewed_at = excluded.reviewed_at, updated_at = excluded.updated_at`, [key, org, offering, s(p.service_model), s(p.impact_level), rank, statusRaw, status, s(p.fedramp_offering_id), rowSha, fileSha, retrieved, snapshot,
        change.source_kind, prov.reviewer, prov.reviewed_at, now, now]);
    return key;
}
/** Applied GovRAMP rows (global public content; facts with a link, never a re-published list). */
export async function listGovrampProducts(content, filter = {}) {
    const rows = (await content.query(`SELECT govramp_product_key, organization_name, service_offering, service_model, impact_level, impact_rank, status_raw, govramp_status,
            fedramp_offering_id, retrieved FROM govramp_products WHERE status = 'active' ORDER BY organization_name, service_offering`)).results;
    return filter.organization ? rows.filter((r) => normName(r.organization_name).includes(normName(filter.organization))) : rows;
}
export async function getGovrampProduct(content, key) {
    return (await content.query(`SELECT govramp_product_key, organization_name, service_offering, service_model, impact_level, impact_rank, status_raw, govramp_status,
            fedramp_offering_id, retrieved FROM govramp_products WHERE govramp_product_key = ? AND status = 'active' LIMIT 1`, [key])).results[0] ?? null;
}
//# sourceMappingURL=govramp-product-list-service.js.map